/**
 * Scopa — server-authoritative rules engine (Italian capture game, four players in two partnerships).
 *
 * Rules implemented (toggles live in {@link ScopaConfig}):
 *  - A 40-card deck (the French 52 without the 8s, 9s and 10s). Card values: ace 1, 2–7 face value, jack 8, queen 9,
 *    king 10. Seats 0 & 2 (team 0) play seats 1 & 3 (team 1).
 *  - Four cards are placed face up on the table, then three cards are dealt to each player. When all hands are empty
 *    three more are dealt, until the deck is gone (nine cards each in all).
 *  - On your turn you play one card. If the table holds a card of the same value you must capture it. Otherwise, if
 *    some of the table cards add up to your card's value you must capture such a set (your choice of set). Only when
 *    neither is possible do you "trail": the card stays on the table.
 *  - Clearing the table with a capture is a scopa (a sweep) worth a point, except on the last play of the deal.
 *    Whoever captured last takes the cards left on the table at the end.
 *  - Scoring per deal, one point each: most cards, most diamonds, the seven of diamonds (settebello), the best
 *    primiera (a card of each suit: 7 = 21, 6 = 18, ace = 16, 5 = 15, 4 = 14, 3 = 13, 2 = 12, face = 10; a team without
 *    a suit loses to one with all four), and one per scopa. The match goes to `targetScore`, continuing past a tie.
 *    Partners share a place.
 */
import { buildDeck, parseCard, removeCards, sortHand, SUITS, type CardId } from '../../core/cards.js';
import { checkInt, mergeKnown } from '../../core/config.js';
import { shuffle, type Rng } from '../../core/rng.js';
import {
  assertEngine,
  EngineError,
  isPlainObject,
  type EngineEvent,
  type GameEngine,
  type Standing,
} from '../../core/types.js';

export interface ScopaConfig {
  targetScore: number;
  /** Hard cap on deals so a match always terminates. */
  maxRounds: number;
  roundEndDelayMs: number;
  moveDelayMs: number;
}

export type ScopaPhase = 'PLAYING' | 'ROUND_END' | 'MATCH_END';

export interface ScopaMove {
  seat: number;
  card: CardId;
  captured: CardId[];
  scopa: boolean;
}

export interface ScopaTeamRound {
  cards: number;
  diamonds: number;
  settebello: boolean;
  primiera: number;
  scopas: number;
  /** Points won this deal, by category. */
  points: { cards: number; diamonds: number; settebello: number; primiera: number; scopas: number };
  total: number;
}

export interface ScopaState {
  config: ScopaConfig;
  players: string[];
  round: number;
  dealer: number;
  phase: ScopaPhase;
  deck: CardId[];
  hands: CardId[][];
  table: CardId[];
  /** Cards captured so far this deal, per team. */
  captured: CardId[][];
  scopas: number[];
  lastCapturer: number | null;
  turn: number;
  lastMove: ScopaMove | null;
  teamScores: number[];
  roundResults: ScopaTeamRound[][];
}

export type ScopaAction = { type: 'play'; card: CardId; capture: CardId[] };

/** For each playable card, the sets of table cards it may capture (an empty list means it can only be trailed). */
export interface ScopaLegal {
  moves: { card: CardId; captures: CardId[][] }[];
}

export interface ScopaView {
  gameType: 'SCOPA';
  config: ScopaConfig;
  seat: number | null;
  phase: ScopaPhase;
  round: number;
  dealer: number;
  turn: number;
  hand: CardId[];
  handCounts: number[];
  table: CardId[];
  deckCount: number;
  capturedCounts: number[];
  scopas: number[];
  lastMove: ScopaMove | null;
  teamScores: number[];
  roundResults: ScopaTeamRound[][];
  totals: number[];
}

const DEFAULTS: ScopaConfig = {
  targetScore: 11,
  maxRounds: 10,
  roundEndDelayMs: 7000,
  moveDelayMs: 0,
};

const SEATS = 4;

export const scopaTeamOf = (seat: number): number => seat % 2;
export const scopaPartnerOf = (seat: number): number => (seat + 2) % SEATS;

/** Card value: ace 1, 2–7 face value, jack 8, queen 9, king 10. */
export const scopaValue = (card: CardId): number => {
  const r = parseCard(card).rank;
  return r === 14 ? 1 : r === 11 ? 8 : r === 12 ? 9 : r === 13 ? 10 : r;
};

const PRIMIERA: Record<number, number> = { 7: 21, 6: 18, 1: 16, 5: 15, 4: 14, 3: 13, 2: 12, 8: 10, 9: 10, 10: 10 };
export const scopaPrimieraValue = (card: CardId): number => PRIMIERA[scopaValue(card)]!;

export function scopaDeck(): CardId[] {
  return buildDeck().filter((c) => {
    const r = parseCard(c).rank;
    return r !== 8 && r !== 9 && r !== 10;
  });
}

/** Every subset of `table` that adds up to `target`. */
export function scopaSumSets(table: readonly CardId[], target: number): CardId[][] {
  const out: CardId[][] = [];
  const vals = table.map(scopaValue);
  const rec = (start: number, sum: number, picked: CardId[]) => {
    if (sum === target) {
      out.push([...picked]);
      return;
    }
    for (let i = start; i < table.length; i++) {
      if (sum + vals[i]! > target) continue;
      picked.push(table[i]!);
      rec(i + 1, sum + vals[i]!, picked);
      picked.pop();
    }
  };
  rec(0, 0, []);
  return out;
}

/** The captures open to a card: an equal-valued table card if there is one, else any set summing to its value. */
export function scopaCaptures(card: CardId, table: readonly CardId[]): CardId[][] {
  const v = scopaValue(card);
  const same = table.filter((c) => scopaValue(c) === v);
  if (same.length > 0) return same.map((c) => [c]);
  return scopaSumSets(table, v);
}

export function scopaLegal(hand: readonly CardId[], table: readonly CardId[]): ScopaLegal {
  return { moves: hand.map((card) => ({ card, captures: scopaCaptures(card, table) })) };
}

/** Primiera total: the best card of each suit, or null when a suit is missing. */
export function scopaPrimiera(cards: readonly CardId[]): { sum: number; complete: boolean } {
  let sum = 0;
  let complete = true;
  for (const suit of SUITS) {
    const best = cards.filter((c) => parseCard(c).suit === suit).reduce((m, c) => Math.max(m, scopaPrimieraValue(c)), 0);
    if (best === 0) complete = false;
    sum += best;
  }
  return { sum, complete };
}

function dealHands(state: ScopaState): void {
  for (let s = 0; s < SEATS; s++) {
    const next = state.deck.splice(0, 3);
    state.hands[s] = sortHand([...(state.hands[s] ?? []), ...next]);
  }
}

function startRound(state: ScopaState, rng: Rng, events: EngineEvent[]): void {
  state.deck = shuffle(scopaDeck(), rng);
  state.table = state.deck.splice(0, 4);
  state.hands = [[], [], [], []];
  dealHands(state);
  state.captured = [[], []];
  state.scopas = [0, 0];
  state.lastCapturer = null;
  state.lastMove = null;
  state.phase = 'PLAYING';
  state.turn = (state.dealer + 1) % SEATS;
  events.push({ type: 'dealt', round: state.round, dealer: state.dealer, table: [...state.table] });
}

/** Scores the deal's categories for both teams. */
export function scopaScoreRound(captured: readonly (readonly CardId[])[], scopas: readonly number[]): ScopaTeamRound[] {
  const stats = [0, 1].map((t) => {
    const cards = captured[t]!;
    return {
      cards: cards.length,
      diamonds: cards.filter((c) => parseCard(c).suit === 'D').length,
      settebello: cards.includes('7D'),
      primiera: scopaPrimiera(cards),
      scopas: scopas[t]!,
    };
  });
  const pts = [0, 1].map(() => ({ cards: 0, diamonds: 0, settebello: 0, primiera: 0, scopas: 0 }));
  if (stats[0]!.cards !== stats[1]!.cards) pts[stats[0]!.cards > stats[1]!.cards ? 0 : 1]!.cards = 1;
  if (stats[0]!.diamonds !== stats[1]!.diamonds) pts[stats[0]!.diamonds > stats[1]!.diamonds ? 0 : 1]!.diamonds = 1;
  for (const t of [0, 1]) if (stats[t]!.settebello) pts[t]!.settebello = 1;
  const [p0, p1] = [stats[0]!.primiera, stats[1]!.primiera];
  if (p0.complete !== p1.complete) pts[p0.complete ? 0 : 1]!.primiera = 1;
  else if (p0.complete && p0.sum !== p1.sum) pts[p0.sum > p1.sum ? 0 : 1]!.primiera = 1;
  for (const t of [0, 1]) pts[t]!.scopas = stats[t]!.scopas;
  return [0, 1].map((t) => ({
    cards: stats[t]!.cards,
    diamonds: stats[t]!.diamonds,
    settebello: stats[t]!.settebello,
    primiera: stats[t]!.primiera.sum,
    scopas: stats[t]!.scopas,
    points: pts[t]!,
    total: pts[t]!.cards + pts[t]!.diamonds + pts[t]!.settebello + pts[t]!.primiera + pts[t]!.scopas,
  }));
}

export const scopaEngine: GameEngine<ScopaConfig, ScopaState, ScopaAction, ScopaView, ScopaLegal> = {
  gameType: 'SCOPA',
  kind: 'match',

  defaultConfig: () => ({ ...DEFAULTS }),

  normalizeConfig(input) {
    const c = mergeKnown(DEFAULTS, input);
    checkInt('targetScore', c.targetScore, 3, 51);
    checkInt('maxRounds', c.maxRounds, 1, 40);
    checkInt('roundEndDelayMs', c.roundEndDelayMs, 0, 60000);
    checkInt('moveDelayMs', c.moveDelayMs, 0, 10000);
    return c;
  },

  seatLimits: () => ({ min: SEATS, max: SEATS }),

  createState(config, seats, rng) {
    assertEngine(seats.length === SEATS, 'INVALID_CONFIG', 'Scopa needs exactly 4 seats');
    const players = Array<string>(SEATS).fill('');
    for (const s of seats) {
      assertEngine(s.seat >= 0 && s.seat < SEATS && !players[s.seat], 'SEAT_TAKEN', `Bad seat ${s.seat}`);
      players[s.seat] = s.playerId;
    }
    const state: ScopaState = {
      config,
      players,
      round: 1,
      dealer: rng.int(SEATS),
      phase: 'PLAYING',
      deck: [],
      hands: [],
      table: [],
      captured: [[], []],
      scopas: [0, 0],
      lastCapturer: null,
      turn: 0,
      lastMove: null,
      teamScores: [0, 0],
      roundResults: [],
    };
    startRound(state, rng, []);
    return state;
  },

  parseAction(raw) {
    if (!isPlainObject(raw)) throw new EngineError('INVALID_ACTION', 'Action must be an object');
    if (raw.type === 'play' && typeof raw.card === 'string' && raw.card.length <= 4) {
      const cap = raw.capture;
      if (cap === undefined) return { type: 'play', card: raw.card, capture: [] };
      if (Array.isArray(cap) && cap.length <= 10 && cap.every((c) => typeof c === 'string' && c.length <= 4)) return { type: 'play', card: raw.card, capture: cap as string[] };
    }
    throw new EngineError('INVALID_ACTION', 'Unknown Scopa action');
  },

  applyAction(state, seat, action) {
    assertEngine(state.phase === 'PLAYING', 'GAME_OVER', 'No move is expected now');
    assertEngine(seat === state.turn, 'NOT_YOUR_TURN', 'Not your turn');
    const events: EngineEvent[] = [];
    const hand = state.hands[seat]!;
    assertEngine(hand.includes(action.card), 'INVALID_CARD', 'Card not in hand');
    const options = scopaCaptures(action.card, state.table);
    const chosen = action.capture;
    assertEngine(new Set(chosen).size === chosen.length, 'ILLEGAL_MOVE', 'Duplicate cards in the capture');

    if (options.length === 0) {
      assertEngine(chosen.length === 0, 'ILLEGAL_MOVE', 'There is nothing to capture with that card');
    } else {
      assertEngine(chosen.length > 0, 'ILLEGAL_MOVE', 'You must capture when you can');
      const key = (cs: readonly CardId[]) => [...cs].sort().join(',');
      assertEngine(options.some((o) => key(o) === key(chosen)), 'ILLEGAL_MOVE', 'That is not a legal capture');
    }

    removeCards(hand, [action.card]);
    const team = scopaTeamOf(seat);
    let scopa = false;
    if (chosen.length > 0) {
      removeCards(state.table, chosen);
      state.captured[team]!.push(action.card, ...chosen);
      state.lastCapturer = seat;
      const lastPlay = state.deck.length === 0 && state.hands.every((h) => h.length === 0);
      if (state.table.length === 0 && !lastPlay) {
        scopa = true;
        state.scopas[team]!++;
      }
    } else {
      state.table.push(action.card);
    }
    state.lastMove = { seat, card: action.card, captured: [...chosen], scopa };
    events.push({ type: chosen.length > 0 ? 'capture' : 'trail', seat, card: action.card, captured: [...chosen], scopa });

    if (state.hands.every((h) => h.length === 0)) {
      if (state.deck.length > 0) {
        dealHands(state);
        events.push({ type: 'dealMore', counts: state.hands.map((h) => h.length), deck: state.deck.length });
      } else {
        endRound(state, events);
        return events;
      }
    }
    state.turn = (seat + 1) % SEATS;
    return events;
  },

  getActors(state) {
    return state.phase === 'PLAYING' ? [state.turn] : [];
  },

  getLegalActions(state, seat) {
    if (state.phase !== 'PLAYING' || seat !== state.turn) return { moves: [] };
    return scopaLegal(state.hands[seat]!, state.table);
  },

  getTimeoutAction(state, seat) {
    const hand = state.hands[seat]!;
    // Capture something if any card can; otherwise trail the lowest card.
    for (const card of hand) {
      const caps = scopaCaptures(card, state.table);
      if (caps.length > 0) return { type: 'play', card, capture: caps[0]! };
    }
    const lowest = [...hand].sort((a, b) => scopaValue(a) - scopaValue(b))[0]!;
    return { type: 'play', card: lowest, capture: [] };
  },

  getView(state, seat) {
    return {
      gameType: 'SCOPA',
      config: state.config,
      seat,
      phase: state.phase,
      round: state.round,
      dealer: state.dealer,
      turn: state.turn,
      hand: seat === null ? [] : [...state.hands[seat]!],
      handCounts: state.hands.map((h) => h.length),
      table: [...state.table],
      deckCount: state.deck.length,
      capturedCounts: state.captured.map((c) => c.length),
      scopas: [...state.scopas],
      lastMove: state.lastMove ? { ...state.lastMove, captured: [...state.lastMove.captured] } : null,
      teamScores: [...state.teamScores],
      roundResults: state.roundResults.map((r) => r.map((x) => ({ ...x, points: { ...x.points } }))),
      totals: [0, 1, 2, 3].map((s) => state.teamScores[scopaTeamOf(s)]!),
    };
  },

  getSystemStep(state) {
    return state.phase === 'ROUND_END' ? { kind: 'nextRound', delayMs: state.config.roundEndDelayMs } : null;
  },

  runSystemStep(state, rng) {
    const events: EngineEvent[] = [];
    if (state.phase === 'ROUND_END') {
      state.round++;
      state.dealer = (state.dealer + 1) % SEATS;
      startRound(state, rng, events);
    }
    return events;
  },

  isFinished: (state) => state.phase === 'MATCH_END',

  getStandings(state): Standing[] {
    return standings(state);
  },

  getRoundsCompleted: (state) => state.roundResults.length,
};

function endRound(state: ScopaState, events: EngineEvent[]): void {
  // The last capturer's side takes whatever is left on the table.
  if (state.table.length > 0 && state.lastCapturer !== null) {
    state.captured[scopaTeamOf(state.lastCapturer)]!.push(...state.table);
    events.push({ type: 'tableSwept', seat: state.lastCapturer, count: state.table.length });
    state.table = [];
  }
  const result = scopaScoreRound(state.captured, state.scopas);
  result.forEach((r, t) => {
    state.teamScores[t]! += r.total;
  });
  state.roundResults.push(result);
  events.push({ type: 'roundEnd', round: state.round, results: result, teamScores: [...state.teamScores] });
  const [a, b] = state.teamScores as [number, number];
  const over = (Math.max(a, b) >= state.config.targetScore && a !== b) || state.round >= state.config.maxRounds;
  if (over) {
    state.phase = 'MATCH_END';
    events.push({ type: 'matchEnd', standings: standings(state) });
  } else {
    state.phase = 'ROUND_END';
  }
}

/** Partners share a place: the higher score is 1st (both 1st on a tie). */
function standings(state: ScopaState): Standing[] {
  const [a, b] = state.teamScores as [number, number];
  return [0, 1, 2, 3].map((seat) => {
    const team = scopaTeamOf(seat);
    const mine = team === 0 ? a : b;
    const theirs = team === 0 ? b : a;
    return { seat, score: mine, place: mine >= theirs ? 1 : 2 };
  });
}
